<template>
  <!-- Absorbs the height the row has left over, so the chart scales instead of overflowing. -->
  <v-card class="py-6 d-flex flex-column" rounded="lg" elevation="0">
    <div class="px-6 subtitle2 mb-4">{{ $t("network.overview.timeseries.title") }}</div>

    <div v-if="loading" class="px-6">
      <v-skeleton-loader type="image" />
    </div>

    <template v-else>
      <div class="px-6 chart-content">
        <line-chart :data="chartData" :options="chartOptions" />
      </div>
      <div class="px-6 d-flex align-center justify-end delta-indicator">
        <span :class="deltaDisplay.cssClass" v-html="deltaDisplay.text"></span>
      </div>
    </template>
  </v-card>
</template>

<script lang="ts">
import { computed, defineComponent, type PropType } from "vue";
import { useI18n } from "vue-i18n";
import { useTheme } from "vuetify";
import type { ChartData, ChartOptions } from "chart.js";
import LineChart, { defaultLineChartOptions } from "@/components/LineChart.vue";
import { getFormattedDateTime } from "@/_helpers/date";
import { NetworkAnalyticsPeriod, NetworkTrendDirection } from "@/constants/network";
import type { NetworkAnalyticsTimeseries } from "@/_store/network/network-analytics.module";

export default defineComponent({
  components: { LineChart },
  props: {
    data: {
      type: Object as PropType<NetworkAnalyticsTimeseries | null>,
      default: null,
    },
    loading: {
      type: Boolean,
      default: false,
    },
    /** Drives the badge's wording — "from last week" / "month" / "quarter". */
    period: {
      type: String as PropType<NetworkAnalyticsPeriod>,
      default: NetworkAnalyticsPeriod.MONTH,
    },
  },
  setup(props) {
    const i18n = useI18n();
    const theme = useTheme();

    const points = computed(() => props.data?.items ?? []);

    // Through the app's moment helper, so the axis follows the locale the user chose rather than the
    // browser's — the same rule every other date on the board follows.
    const labels = computed(() =>
      points.value.map((point) => getFormattedDateTime(point.bucketStart, "MMM D"))
    );

    /**
     * A bucket with a value but no drawable neighbour.
     *
     * Chart.js draws a line only *between* consecutive non-null points, so with `spanGaps: false` and no
     * point marker such a bucket paints nothing at all — an empty canvas while the store holds real data.
     * That is the expected shape whenever the held history starts mid-range, and for the first and last
     * bucket of any partially held window.
     */
    const isIsolated = (index: number) => {
      const values = points.value;
      if (values[index]?.count === null || values[index]?.count === undefined) return false;
      const previous = values[index - 1]?.count;
      const next = values[index + 1]?.count;
      return (previous === null || previous === undefined) && (next === null || next === undefined);
    };

    const chartData = computed<ChartData<"line">>(() => ({
      labels: labels.value,
      datasets: [
        {
          // Buckets outside the held history arrive as null and Chart.js renders them as a gap,
          // which is the honest representation of "no data" — a zero would read as "nothing happened".
          data: points.value.map((point) => point.count),
          borderColor: theme.current.value.colors["blue-light"],
          backgroundColor: theme.current.value.colors["blue-light"],
          borderWidth: 1.5,
          // Markers are off by default and turned on only where a line cannot be drawn.
          pointRadius: (context) => (isIsolated(context.dataIndex) ? 3 : 0),
          pointHoverRadius: 5,
          pointHoverBackgroundColor: "white",
          tension: 0.4,
          spanGaps: false,
        },
      ],
    }));

    const chartOptions = computed<ChartOptions<"line">>(() => ({
      ...defaultLineChartOptions,
      plugins: {
        ...defaultLineChartOptions.plugins,
        legend: { display: false },
      },
      scales: {
        ...defaultLineChartOptions.scales,
        y: {
          beginAtZero: true,
          grid: { color: theme.current.value.colors["indigo-faint"] },
          ticks: { color: theme.current.value.colors["indigo-medium"], maxTicksLimit: 3 },
        },
        x: {
          grid: { display: false },
          ticks: { display: false },
        },
      },
    }));

    /**
     * Whether anything at all falls in the current window.
     *
     * <p>Read off the buckets rather than summed: these are unique devices, and a device present in two
     * buckets is one device, not two. Only "is it zero" is needed, and for that the buckets are exact.
     */
    const hasCurrent = computed(() =>
      (props.data?.items ?? []).some((point) => (point.count ?? 0) > 0)
    );

    /** Which way the badge points, preferring the backend's own answer over the local arithmetic. */
    const toneOf = (
      direction: NetworkTrendDirection | null,
      percent: number
    ): "up" | "down" | "flat" => {
      if (direction === NetworkTrendDirection.UP) return "up";
      if (direction === NetworkTrendDirection.DOWN) return "down";
      if (direction === NetworkTrendDirection.FLAT) return "flat";
      if (percent > 0) return "up";
      return percent < 0 ? "down" : "flat";
    };

    /**
     * Always renders. The backend reports a null percentage when it cannot compute one — an empty
     * comparison window, or one predating the data it holds — and the board still has to say something,
     * so those cases are given a reading here rather than hiding the badge.
     */
    const deltaDisplay = computed(() => {
      const trend = props.data?.trend;
      // A null previousTotal means the window is not held at all; for this badge that reads the same as
      // nothing having happened in it.
      const previous = trend?.previousTotal ?? 0;

      let value: string;
      let tone: "up" | "down" | "flat";

      if (previous === 0) {
        // Growth from nothing is a division by zero, not a large number, so it is shown as such rather
        // than as an invented percentage.
        value = hasCurrent.value ? "&#8734;%" : "0%";
        tone = hasCurrent.value ? "up" : "flat";
      } else {
        const percent = trend?.changePercent ?? 0;
        // A decimal below 1%, whole numbers above it: rounding a -0.4% fall to "0%" hides it entirely,
        // and rounding is also what turns it into -0, which is neither > 0 nor < 0 and so used to be
        // read as flat and given an up arrow.
        const magnitude = Math.abs(percent);
        value = `${magnitude < 1 && magnitude > 0 ? magnitude.toFixed(1) : Math.round(magnitude)}%`;
        // The backend already answers this in three states; re-deriving it from the rounded percentage
        // only adds a second, less accurate opinion. Falling back to the raw sign - not the rounded one -
        // for the case where it does not report a direction at all.
        tone = toneOf(trend?.direction ?? null, percent);
      }

      return {
        // Up arrow for everything that is not a fall, including a flat 0%.
        text: `${tone === "down" ? "&#8595;" : "&#8593;"} ${i18n.t(
          `network.overview.timeseries.fromLast.${props.period}`,
          { value }
        )}`,
        cssClass:
          tone === "up"
            ? "text-green-dark"
            : tone === "down"
              ? "text-red-dark"
              : "text-indigo-medium",
      };
    });

    return { chartData, chartOptions, deltaDisplay };
  },
});
</script>

<style scoped lang="scss">
.chart-content {
  // Grows into the free space of the column; min-height 0 lets it shrink below the canvas's
  // intrinsic size, and position: relative gives Chart.js a box to size its canvas against.
  flex: 1 1 auto;
  min-height: 120px;
  position: relative;
}

.delta-indicator {
  font-size: 12px;
}
</style>
